import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api/apiClient';
import { getAssetUrl } from '../../utils/apiBase';
import { isOrgLearner } from '../../utils/userType';
import mpsLogo from '../../assets/images/mps_logo.png';
import { 
  FaTachometerAlt, 
  FaBookOpen, 
  FaGraduationCap, 
  FaLaptop, 
  FaCertificate, 
  FaComments, 
  FaPoll, 
  FaReceipt, 
  FaHeadset,
  FaClipboardCheck,
  FaCalendarAlt,
  FaTrophy,
  FaMedal,
  FaFileContract,
  FaShieldAlt,
  FaQuestionCircle,
  FaRoute
} from 'react-icons/fa';

// Menu IDs always shown in the user panel (e.g. newly added items missing from older RBAC configs)
const ALWAYS_VISIBLE_USER_MENU_IDS = [10, 11, 12, 13, 14, 15, 16]; // Calendar, Leaderboard, Badges, Terms, Privacy, FAQ, Learning Journey

// All possible user panel menus — id matches menuConfig.js USER_MENU_ITEMS
// Order and labels match menuConfig.js USER_MENU_ITEMS
const ALL_MENU_ITEMS = [
  { id: 0, icon: FaTachometerAlt,  label: 'Dashboard',               path: '/user/dashboard',        color: 'from-purple-500 to-purple-700' },
  { id: 1, icon: FaBookOpen,       label: 'Browse Courses',          path: '/user/courses',           color: 'from-blue-500 to-blue-700' },
  { id: 2, icon: FaGraduationCap,  label: 'My Courses',              path: '/user/enrolled-courses',  color: 'from-emerald-500 to-emerald-700' },
  { id: 7, icon: FaClipboardCheck, label: 'My Assessments',          path: '/user/assessments',       color: 'from-lime-500 to-lime-700' },
  { id: 3, icon: FaLaptop,         label: 'My Virtual-Led Training', path: '/user/vlt',               color: 'from-indigo-500 to-indigo-700' },
  { id: 16, icon: FaRoute,         label: 'My Learning Journey',        path: '/user/learning-journeys', color: 'from-violet-500 to-indigo-700' },
  { id: 4, icon: FaCertificate,    label: 'My Certificates',         path: '/user/certificates',      color: 'from-amber-500 to-amber-700' },
  { id: 10, icon: FaCalendarAlt,   label: 'My Event Calendar',       path: '/user/calendar',          color: 'from-violet-500 to-violet-700' },
  { id: 11, icon: FaTrophy,        label: 'Leaderboard',             path: '/user/leaderboard',       color: 'from-yellow-500 to-orange-600' },
  { id: 12, icon: FaMedal,         label: 'Badges',                  path: '/user/badges',            color: 'from-rose-500 to-red-600' },
  { id: 5, icon: FaComments,       label: 'Forum Discussions',       path: '/user/forums',            color: 'from-cyan-500 to-cyan-700' },
  { id: 6, icon: FaPoll,           label: 'Surveys',                 path: '/user/surveys',           color: 'from-pink-500 to-pink-700' },
  { id: 8, icon: FaReceipt,        label: 'My Transactions',         path: '/user/transactions',      color: 'from-teal-500 to-teal-700' },
  { id: 9, icon: FaHeadset,        label: 'Support Tickets',         path: '/user/support-tickets',   color: 'from-red-500 to-red-700' },
];

const getActiveIndex = (pathname, items) => {
  if (pathname.startsWith('/user/course-modules')) {
    return items.findIndex((item) => item.path === '/user/enrolled-courses');
  }

  if (pathname.startsWith('/user/cart')) {
    return items.findIndex((item) => item.path === '/user/courses');
  }

  if (pathname.startsWith('/user/learning-journeys')) {
    return items.findIndex((item) => item.path === '/user/learning-journeys');
  }

  const matchedIndex = items.findIndex((item) => pathname.startsWith(item.path));
  return matchedIndex >= 0 ? matchedIndex : 0;
};

const UserSidebar = ({ onTabChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const roleValue = user ? parseInt(user.role) : null;

  // null = no filter (full access); [] = no menus; [ids] = allowed
  const [allowedMenuIds, setAllowedMenuIds] = useState(null);

  const menuItems = allowedMenuIds === null
    ? ALL_MENU_ITEMS
    : ALL_MENU_ITEMS.filter((item) => (
      allowedMenuIds.includes(item.id) || ALWAYS_VISIBLE_USER_MENU_IDS.includes(item.id)
    ));

  const [activeTab, setActiveTab] = useState(() => getActiveIndex(location.pathname, menuItems));
  const [orgLogoUrl, setOrgLogoUrl] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgLogoResolved, setOrgLogoResolved] = useState(false);

  const isOrg = isOrgLearner(user);

  // Fetch access config for this role
  useEffect(() => {
    if (roleValue === null) return;
    api.accessControl.getByRole(roleValue)
      .then((res) => {
        if (res.data?.success) {
          const { full_access, menu_ids } = res.data.data;
          setAllowedMenuIds(full_access ? null : (menu_ids ?? []));
        }
      })
      .catch(() => setAllowedMenuIds(null)); // on error show all
  }, [roleValue]);

  useEffect(() => {
    setActiveTab(getActiveIndex(location.pathname, menuItems));
  }, [location.pathname, menuItems]);

  useEffect(() => {
    if (!isOrg || !user?.organization_id) {
      setOrgLogoUrl('');
      setOrgName('');
      setOrgLogoResolved(true);
      return;
    }

    let cancelled = false;
    setOrgLogoResolved(false);

    const loadOrganizationLogo = async () => {
      try {
        const response = await api.organizations.getById(user.organization_id);
        if (cancelled) return;

        const org = response.data;
        setOrgName(org?.name || '');
        setOrgLogoUrl(org?.logo_path ? getAssetUrl(org.logo_path) : '');
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load organization logo:', error);
          setOrgLogoUrl('');
          setOrgName('');
        }
      } finally {
        if (!cancelled) {
          setOrgLogoResolved(true);
        }
      }
    };

    loadOrganizationLogo();

    return () => {
      cancelled = true;
    };
  }, [isOrg, user?.organization_id]);

  const handleTabClick = (tabIndex, path) => {
    setActiveTab(tabIndex);
    navigate(path);
    if (onTabChange) onTabChange();
  };

  const useOrgLogo = isOrg && Boolean(orgLogoUrl);
  const sidebarLogoSrc = useOrgLogo ? orgLogoUrl : mpsLogo;
  const sidebarLogoAlt = useOrgLogo
    ? (orgName ? `${orgName} logo` : 'Organization logo')
    : 'Enable LXP Logo';

  return (
    <div className="shadow-1xl">
      {/* Glass Header */}
      <div className="bg-[#fefcff] border-none p-2 flex justify-center items-center min-h-[4.5rem]">
        {isOrg && !orgLogoResolved ? (
          <div className="w-32 h-14" aria-hidden="true" />
        ) : (
          <img
            src={sidebarLogoSrc}
            alt={sidebarLogoAlt}
            className="w-32 h-14 object-contain drop-shadow-md transition-transform hover:scale-105"
          />
        )}
      </div>

      {/* Menu */}
      <div className="flex-1 overflow-y-auto scrollbar-thin brand-app-sidebar-scroll brand-app-sidebar border-r">
        <div className="py-3 px-2">
          {menuItems.map((item, index) => {
            const IconComponent = item.icon;
            const isActive = activeTab === index;
            
            return (
              <div
                key={index}
                onClick={() => handleTabClick(index, item.path)}
                className={`    
             flex items-center cursor-pointer font-medium text-sm
              min-h-12 px-2 py-2  rounded-xl group
              ${isActive 
                ? 'bg-white/30 text-white shadow-md border-l-4 border-white ' 
                : 'text-white hover:bg-white/15 hover:text-white '
              }
                `}
              >
                <div className={`me-3 w-9 h-9 rounded-xl bg-gradient-to-br ${item.color} p-2 shadow-lg group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300 flex items-center justify-center`}>
                  <IconComponent className="w-5 h-5 text-white" />
                </div>
                <span className={`font-medium text-sm transition-colors ${
                  isActive ? 'text-white font-semibold' : 'text-white/90 group-hover:text-white'
                }`}>
                  {item.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default UserSidebar;
